import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  practicesService,
  CreatePracticeInput,
  UpdatePracticeInput,
  AddPracticePhysicianInput,
  CreatePracticeReportDeliveryMethodInput,
  UpdatePracticeReportDeliveryMethodInput,
} from "../../services/admin/practices.service";

// Returns true when an error is the backend's "subject has no active portal grants
// for the requested portal delivery method" response. The component renders an
// inline setup prompt for this case, so the default red toast must be suppressed.
function isNoPortalAccessError(error: unknown): boolean {
  const response = (
    error as { response?: { status?: number; data?: { error?: string } } }
  )?.response;
  return (
    response?.status === 409 && response?.data?.error === "NO_PORTAL_ACCESS"
  );
}

// ============================================
// PRACTICE CRUD HOOKS
// ============================================

export function usePractices() {
  return useQuery({
    queryKey: ["admin", "practices"],
    queryFn: practicesService.getAll,
  });
}

export function useActivePractices() {
  return useQuery({
    queryKey: ["admin", "practices", "active"],
    queryFn: async () => {
      const practices = await practicesService.getAll();
      return practices.filter((p) => p.isActive);
    },
  });
}

export function usePractice(id?: string | null) {
  return useQuery({
    queryKey: ["admin", "practices", id],
    queryFn: () => practicesService.getById(id!),
    enabled: !!id,
  });
}

export function useCreatePractice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreatePracticeInput) => practicesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "practices"] });
      // Also invalidate the reference data used in accessioning dropdowns
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      notifications.show({
        title: "Success",
        message: "Practice created successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create practice",
        color: "red",
      });
    },
  });
}

export function useUpdatePractice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdatePracticeInput) => practicesService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "practices"] });
      notifications.show({
        title: "Success",
        message: "Practice updated successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update practice",
        color: "red",
      });
    },
  });
}

export function useDeletePractice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => practicesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "practices"] });
      notifications.show({
        title: "Success",
        message: "Practice deleted successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete practice",
        color: "red",
      });
    },
  });
}

export function useTogglePracticeActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      practicesService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "practices"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}

// ============================================
// PRACTICE-PHYSICIAN HOOKS
// ============================================

export function usePracticePhysicians(practiceId: string | null) {
  return useQuery({
    queryKey: ["admin", "practices", practiceId, "physicians"],
    queryFn: () => practicesService.getPhysicians(practiceId!),
    enabled: !!practiceId,
  });
}

export function useAddPracticePhysician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      practiceId,
      input,
    }: {
      practiceId: string;
      input: AddPracticePhysicianInput;
    }) => practicesService.addPhysician(practiceId, input),
    onSuccess: (_, { practiceId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "practices", practiceId, "physicians"],
      });
      notifications.show({
        title: "Success",
        message: "Physician linked successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to link physician",
        color: "red",
      });
    },
  });
}

export function useRemovePracticePhysician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      practiceId,
      physicianId,
    }: {
      practiceId: string;
      physicianId: string;
    }) => practicesService.removePhysician(practiceId, physicianId),
    onSuccess: (_, { practiceId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "practices", practiceId, "physicians"],
      });
      notifications.show({
        title: "Success",
        message: "Physician unlinked successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to unlink physician",
        color: "red",
      });
    },
  });
}

export function useSetPrimaryPracticePhysician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      practiceId,
      physicianId,
    }: {
      practiceId: string;
      physicianId: string;
    }) => practicesService.setPrimaryPhysician(practiceId, physicianId),
    onSuccess: (_, { practiceId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "practices", practiceId, "physicians"],
      });
      notifications.show({
        title: "Success",
        message: "Primary physician updated",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to set primary physician",
        color: "red",
      });
    },
  });
}

// ============================================
// PRACTICE REPORT DELIVERY METHOD HOOKS
// ============================================

export function usePracticeReportDeliveryMethods(practiceId: string | null) {
  return useQuery({
    queryKey: ["admin", "practices", practiceId, "report-delivery-methods"],
    queryFn: () => practicesService.getReportDeliveryMethods(practiceId!),
    enabled: !!practiceId,
  });
}

export function useAddPracticeReportDeliveryMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      practiceId,
      input,
    }: {
      practiceId: string;
      input: CreatePracticeReportDeliveryMethodInput;
    }) => practicesService.addReportDeliveryMethod(practiceId, input),
    onSuccess: (_, { practiceId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "practices", practiceId, "report-delivery-methods"],
      });
      notifications.show({
        title: "Success",
        message: "Delivery method added successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      if (isNoPortalAccessError(error)) {
        // Inline alert in the modal handles this case — no global toast.
        return;
      }
      notifications.show({
        title: "Error",
        message: error.message || "Failed to add delivery method",
        color: "red",
      });
    },
  });
}

export function useUpdatePracticeReportDeliveryMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      practiceId,
      input,
    }: {
      practiceId: string;
      input: UpdatePracticeReportDeliveryMethodInput;
    }) => practicesService.updateReportDeliveryMethod(practiceId, input),
    onSuccess: (_, { practiceId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "practices", practiceId, "report-delivery-methods"],
      });
      notifications.show({
        title: "Success",
        message: "Delivery method updated successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      if (isNoPortalAccessError(error)) {
        return;
      }
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update delivery method",
        color: "red",
      });
    },
  });
}

export function useRemovePracticeReportDeliveryMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ practiceId, id }: { practiceId: string; id: string }) =>
      practicesService.removeReportDeliveryMethod(practiceId, id),
    onSuccess: (_, { practiceId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "practices", practiceId, "report-delivery-methods"],
      });
      notifications.show({
        title: "Success",
        message: "Delivery method removed successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to remove delivery method",
        color: "red",
      });
    },
  });
}

export function useTogglePracticeReportDeliveryMethodActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ practiceId, id }: { practiceId: string; id: string }) =>
      practicesService.toggleReportDeliveryMethodActive(practiceId, id),
    onSuccess: (_, { practiceId }) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "practices", practiceId, "report-delivery-methods"],
      });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}
